import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { UpcomingEventsScroller } from './components/UpcomingEventsScroller';
import { LandingPage } from './components/LandingPage';
import { FoundingFundersPopup } from './components/FoundingFundersPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const NationalNetworkPage = lazy(() => import('./pages/NationalNetworkPage').then(m => ({
  default: m.NationalNetworkPage
})));
const EncuentroCatrachoMiamiPage = lazy(() => import('./pages/EncuentroCatrachoMiamiPage').then(m => ({
  default: m.EncuentroCatrachoMiamiPage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
const HonduranIndependenceDayPage = lazy(() => import('./pages/HonduranIndependenceDayPage').then(m => ({
  default: m.HonduranIndependenceDayPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="c6ermz8ak5bl">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="19vhtrwcj6kbv">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="14x6jxlm8k9tn" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="14v3vh60qeor1" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="vvp484cqqa5h" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="2yi37h2q4lx7b" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="3i4xx2gb01oto">
      <Navbar data-id="element-7" data-mp="3n2zih7vwe08b" />
      <FoundingFundersPopup data-id="element-8" data-mp="3n1enxi9l2hag" />
      <main data-id="element-9" data-mp="3n0n392hiy3yy">
        <Hero data-id="element-10" data-mp="3n0dbb9cx5mqm" />
        <UpcomingEventsScroller data-id="element-11" data-mp="385p8y42tf4f8" />
        <ConsularBanner data-id="element-12" data-mp="384fb9fzvjy94" />
        <Mission data-id="element-13" data-mp="385762olndryc" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-15" data-mp="3d6cw2enxb146" />} data-id="element-14" data-mp="3d2p77stfb2vs">
          <OurPeopleStories data-id="element-16" data-mp="2zclr7jl505es" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-18" data-mp="2zbajj8okmdie" />} data-id="element-17" data-mp="2zbu46x5lvg04">
          <MakeDifferenceCTA data-id="element-19" data-mp="344dixxg14h6i" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-20" data-mp="3456gc7t3lubz">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-21" data-mp="2pfh8jtqi2s2q" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-23" data-mp="2uek5eqwbtrzm" />} data-id="element-22" data-mp="2uf3oiaxpqmf8">
          <NewsBlog data-id="element-24" data-mp="2ubplr1l50b79" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-26" data-mp="2gfkhxmkjvj51" />} data-id="element-25" data-mp="2ge0zbpbf8w03">
          <MediaScroller data-id="element-27" data-mp="2ggdr0u6zekqc" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-29" data-mp="2ketd8vv8np2p" />} data-id="element-28" data-mp="2kge86mlgta13">
          <CommunityStrength data-id="element-30" data-mp="2kf34snvxm4ai" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-32" data-mp="30uc4psvdzqh2" />} data-id="element-31" data-mp="30vwznjlm5bfg">
          <MissionPanels data-id="element-33" data-mp="30ulx1n3wm9px" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-35" data-mp="2vxc3pwde9dwt" />} data-id="element-34" data-mp="2vvr8s5n63syf">
          <Leadership data-id="element-36" data-mp="2vx289t9kyejq" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-38" data-mp="2qwei19t6e1qk" />} data-id="element-37" data-mp="2qwxuwc3yxzxq">
          <AdvisoryCouncil data-id="element-39" data-mp="2qvmridefqu75" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-41" data-mp="2lyw8pib8xki1" />} data-id="element-40" data-mp="2lyd0itbeeah7">
          <MakeDifferenceCTA data-id="element-42" data-mp="2lxl3rf62e6pc" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-44" data-mp="2hwrg8mwjatwz" />} data-id="element-43" data-mp="2hw9fpe2u9tj9">
          <BusinessInvestments data-id="element-45" data-mp="2hy2kentw63om" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-47" data-mp="2d5jb4328lupm" />} data-id="element-46" data-mp="2d62nz5d15sws">
          <ConsularAffairs data-id="element-48" data-mp="2d4829otxpwrn" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-50" data-mp="285wxvtmesegt" />} data-id="element-49" data-mp="286gcb0cuokq3">
          <SocialMediaVoices data-id="element-51" data-mp="2877vtct6as00" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-53" data-mp="238emlwkx5n5n" />} data-id="element-52" data-mp="237v9qua4loyh">
          <NonprofitDirectory data-id="element-54" data-mp="2396d4sznsup2" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-56" data-mp="1y8s9ro9068xd" />} data-id="element-55" data-mp="1y8aasjuyhglr">
          <HondurasQuiz data-id="element-57" data-mp="1y80igpmfuxcw" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-59" data-mp="1u6oueoj7qk6t" />} data-id="element-58" data-mp="1u76zmanus8qv">
          <ExploreHonduras data-id="element-60" data-mp="1u7go1tt927ug" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-62" data-mp="9po0qwqi8u05" />} data-id="element-61" data-mp="9p66g5nekpuv">
          <FAQ data-id="element-63" data-mp="9ocvstlbpg7g" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-64" data-mp="dq7wbvvfjpk6">
        <Footer data-id="element-65" data-mp="dpgd7kj0rkas" />
      </Suspense>
      <Suspense fallback={null} data-id="element-66" data-mp="dsko10qzpeaf">
        <ChatbaseWidget data-id="element-67" data-mp="ipuaq8mzvbuk" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  // The site opens directly on the English main page; language is switched from
  // the toggle in the navigation bar rather than a separate entry screen.
  const [showLanding, setShowLanding] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showEncuentroMiami, setShowEncuentroMiami] = useState(() => window.location.hash === '#/encuentro-catracho-miami');
  const [showIndependenceDay, setShowIndependenceDay] = useState(() => window.location.hash === '#/independence-day');
  const [showNationalNetwork, setShowNationalNetwork] = useState(() => window.location.hash === '#/national-network');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      setShowEncuentroMiami(hash === '#/encuentro-catracho-miami');
      setShowIndependenceDay(hash === '#/independence-day');
      setShowNationalNetwork(hash === '#/national-network');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    // Language switching keeps the visitor on the current page.
    setShowLanding(false);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-69" data-mp="1jc6zqo366487" />} data-id="element-68" data-mp="1ef6pe0zaefba">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-70" data-mp="1jcyigybo47h2" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-71" data-mp="1yfgoewkzqo5u">
        <Suspense fallback={<SectionLoader data-id="element-73" data-mp="1tfklz1rxz4se" />} data-id="element-72" data-mp="1tg3u5qrsiet8">
          <ConsularPage data-id="element-74" data-mp="1tfcbfgjakbkd" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-75" data-mp="1oharyuhyea8d">
        <Suspense fallback={<SectionLoader data-id="element-77" data-mp="2h5evb9d1yb7w" />} data-id="element-76" data-mp="1khs35nn0hf09">
          <BusinessMapPage data-id="element-78" data-mp="2h4n4scybb3oh" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-79" data-mp="2c750x0z5jenc">
        <Suspense fallback={<SectionLoader data-id="element-81" data-mp="27ekam9fgfjok" />} data-id="element-80" data-mp="27f3isyfaytpe">
          <MembersDirectoryPage data-id="element-82" data-mp="27dsfezprrnyt" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-83" data-mp="2zw652c0yc3mt">
        <Suspense fallback={<SectionLoader data-id="element-85" data-mp="2zy0qrsk1rzry" />} data-id="element-84" data-mp="2zxh64430ixa8">
          <VoicesOfImpactNYPage data-id="element-86" data-mp="2v4xuhcnvyc82" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showEncuentroMiami) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-87" data-mp="t98h6pa23bhb">
        <Suspense fallback={<SectionLoader data-id="element-89" data-mp="y7a2lguyfmvy" />} data-id="element-88" data-mp="y6qpqek5voos">
          <EncuentroCatrachoMiamiPage data-id="element-90" data-mp="y6gy6mjgx9gz" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showIndependenceDay) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-91" data-mp="oh79lgnu28xm">
        <Suspense fallback={<SectionLoader data-id="element-93" data-mp="oeuhwbs9wk7d" />} data-id="element-92" data-mp="ofe0zvtntemz">
          <HonduranIndependenceDayPage data-id="element-94" data-mp="agwhkaaseoav" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNationalNetwork) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-95" data-mp="fhaeaw6xubtl">
        <Suspense fallback={<SectionLoader data-id="element-97" data-mp="kba7ibap13v" />} data-id="element-96" data-mp="jrr3y9ws6o9">
          <NationalNetworkPage data-id="element-98" data-mp="l2tpurmb8ds" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-99" data-mp="5k5zjhff9imt">
        <Suspense fallback={<SectionLoader data-id="element-101" data-mp="1uv6h8g6u084j" />} data-id="element-100" data-mp="1uxspki1jqrnt">
          <SaveTheDatePage data-id="element-102" data-mp="1zw3zesrud261" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-103" data-mp="3kdmyo7t3qixa">
        <Suspense fallback={<SectionLoader data-id="element-105" data-mp="3fftt9gzmhsy2" />} data-id="element-104" data-mp="3ffbo1uuzg4e0">
          <MemberProfilePage slug={memberSlug} data-id="element-106" data-mp="3fgmrftkina4l" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-107" data-mp="3u6asctosc6rs">
        <Suspense fallback={<SectionLoader data-id="element-109" data-mp="3pcptz0ja5as2" />} data-id="element-108" data-mp="3pd7ui9cz6b5s">
          <NewsStoryPage postId={newsPostId} data-id="element-110" data-mp="3pbepszlxa10f" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-111" data-mp="2xwxs3jeyuqz" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-112" data-mp="2yoiukfdxa30">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-113" data-mp="309gwk0wrb5m" />
      </LanguageProvider>
    </>;
}